import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Eye, MoreHorizontal, PencilLine, ListFilter, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@ui/dropdown-menu.tsx';
import { Button } from '@ui/button.tsx';
import { UserFullname } from '@r/components/user-fullname.tsx';
import type { SegmentSegmentCollectionReadModel } from '@r/api/generated/client.schemas.ts';

type Segment = SegmentSegmentCollectionReadModel;

/**
 * Définit les colonnes du tableau des segments, avec un menu d'actions optionnel
 * (prévisualiser, modifier, gérer les règles, supprimer).
 */
export const useSegmentsColumns = (
  onPreview?: (segment: Segment) => void,
  onEditDetails?: (segment: Segment) => void,
  onManageRules?: (segment: Segment) => void,
  onDelete?: (segment: Segment) => void
): ColumnDef<Segment>[] => {
  const { t } = useTranslation();

  const columns: ColumnDef<Segment>[] = [
    {
      accessorKey: 'name',
      header: t('admin.segments.column.name'),
      cell: ({ row }) => <span className="font-bold">{row.original.name}</span>
    },
    {
      accessorKey: 'description',
      header: t('admin.segments.column.description'),
      cell: ({ row }) => row.original.description || '—'
    },
    {
      accessorKey: 'createdAt',
      header: t('admin.segments.column.created.at'),
      cell: ({ row }) =>
        row.original.createdAt ? format(new Date(row.original.createdAt), 'dd/MM/yyyy') : ''
    },
    {
      accessorKey: 'createdBy',
      header: t('admin.segments.column.created.by'),
      cell: ({ row }) => (
        <UserFullname
          fullname={row.original.createdBy?.user?.fullname ?? ''}
          isDeleted={row.original.createdBy?.user?.isDeleted}
        />
      )
    }
  ];

  if (onPreview || onEditDetails || onManageRules || onDelete) {
    columns.push({
      id: 'actions',
      cell: ({ row }) => {
        const segment = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex h-8 w-8 p-0 data-[state=open]:bg-muted float-end"
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onPreview && (
                <DropdownMenuItem onClick={() => onPreview(segment)}>
                  <Eye className="mr-2 w-4 h-4" />
                  {t('admin.segments.action.preview')}
                </DropdownMenuItem>
              )}

              {onEditDetails && (
                <DropdownMenuItem onClick={() => onEditDetails(segment)}>
                  <PencilLine className="mr-2 w-4 h-4" />
                  {t('admin.segments.action.edit')}
                </DropdownMenuItem>
              )}

              {onManageRules && (
                <DropdownMenuItem onClick={() => onManageRules(segment)}>
                  <ListFilter className="mr-2 w-4 h-4" />
                  {t('admin.segments.action.rules')}
                </DropdownMenuItem>
              )}

              {(onPreview || onEditDetails || onManageRules) && onDelete && (
                <DropdownMenuSeparator />
              )}

              {onDelete && (
                <DropdownMenuItem
                  onClick={() => onDelete(segment)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 w-4 h-4" />
                  {t('admin.segments.action.delete')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      }
    });
  }

  return columns;
};
